/**
 * CategoryPickerSheet — bottom sheet with 3-level Thai category drill-down.
 *
 * Opens when user taps the Category row in Product Info card.
 * Fetches categories from TikTok API (flat list), builds tree client-side,
 * and renders an animated drill-down picker.
 *
 * Only leaf categories are selectable. INVITE_ONLY categories show lock icon.
 */

import { useState, useMemo, useCallback } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ChevronRight, ChevronLeft, Check, Lock, Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  tiktokProductApi,
  type TikTokCategory,
} from "../api/tiktokProductApi";

// ── Types ──────────────────────────────────────────────────────────

interface CategoryNode extends TikTokCategory {
  children: CategoryNode[];
}

export interface CategorySelection {
  categoryId: string;
  categoryChain: Array<{
    id: string;
    parent_id: string;
    local_name: string;
    is_leaf: boolean;
  }>;
}

interface CategoryPickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionName: string;
  onSelect: (selection: CategorySelection) => void;
}

// ── Tree builder (pure function) ───────────────────────────────────

function buildCategoryTree(categories: TikTokCategory[]): CategoryNode[] {
  const map = new Map<string, CategoryNode>();
  const roots: CategoryNode[] = [];

  // First pass: create nodes
  for (const cat of categories) {
    map.set(cat.id, { ...cat, children: [] });
  }

  // Second pass: link children to parents
  for (const cat of categories) {
    const node = map.get(cat.id)!;
    if (cat.parent_id === "0") {
      roots.push(node);
    } else {
      map.get(cat.parent_id)?.children.push(node);
    }
  }

  return roots;
}

// ── Component ──────────────────────────────────────────────────────

export function CategoryPickerSheet({
  open,
  onOpenChange,
  connectionName,
  onSelect,
}: CategoryPickerSheetProps) {
  // Navigation state: stack of parent nodes we've drilled into
  const [pathStack, setPathStack] = useState<CategoryNode[]>([]);
  // Currently selected leaf
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["tiktok", "categories", connectionName],
    queryFn: () => tiktokProductApi.getCategories(connectionName),
    enabled: open && !!connectionName,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const tree = useMemo(
    () => (categories ? buildCategoryTree(categories) : []),
    [categories],
  );

  // Current level's items
  const currentItems = useMemo(() => {
    if (pathStack.length === 0) return tree;
    return pathStack[pathStack.length - 1].children;
  }, [tree, pathStack]);

  // Breadcrumb path names
  const breadcrumb = useMemo(
    () => pathStack.map((n) => n.local_name),
    [pathStack],
  );

  const handleDrillDown = useCallback((node: CategoryNode) => {
    if (node.is_leaf) return;
    setPathStack((prev) => [...prev, node]);
    setSelectedId(null);
  }, []);

  const handleBack = useCallback(() => {
    setPathStack((prev) => prev.slice(0, -1));
    setSelectedId(null);
  }, []);

  const handleSelectLeaf = useCallback((node: CategoryNode) => {
    setSelectedId(node.id);
  }, []);

  const handleDone = useCallback(() => {
    if (!selectedId || !categories) return;

    // Build the full chain: pathStack parents + selected leaf
    const chain = [
      ...pathStack.map((n) => ({
        id: n.id,
        parent_id: n.parent_id,
        local_name: n.local_name,
        is_leaf: n.is_leaf,
      })),
    ];

    // Find the selected leaf node in current items
    const leaf = currentItems.find((n) => n.id === selectedId);
    if (leaf) {
      chain.push({
        id: leaf.id,
        parent_id: leaf.parent_id,
        local_name: leaf.local_name,
        is_leaf: leaf.is_leaf,
      });
    }

    onSelect({ categoryId: selectedId, categoryChain: chain });
    onOpenChange(false);

    // Reset navigation for next open
    setPathStack([]);
    setSelectedId(null);
  }, [selectedId, categories, pathStack, currentItems, onSelect, onOpenChange]);

  const handleClose = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        // Reset state on close
        setPathStack([]);
        setSelectedId(null);
      }
      onOpenChange(isOpen);
    },
    [onOpenChange],
  );

  const isInviteOnly = (node: CategoryNode) =>
    node.permission_statuses?.includes("INVITE_ONLY");

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-2 border-b border-border/40">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg">Select Category</SheetTitle>
            <button
              type="button"
              className="text-sm font-medium text-primary disabled:text-muted-foreground disabled:cursor-not-allowed"
              disabled={!selectedId}
              onClick={handleDone}
            >
              Done
            </button>
          </div>

          {/* Breadcrumb */}
          {breadcrumb.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 overflow-x-auto">
              <span className="shrink-0">All</span>
              {breadcrumb.map((name, i) => (
                <span key={i} className="flex items-center gap-1 shrink-0">
                  <ChevronRight className="h-3 w-3" />
                  <span>{name}</span>
                </span>
              ))}
            </div>
          )}
        </SheetHeader>

        {/* Back button */}
        {pathStack.length > 0 && (
          <button
            type="button"
            className="flex items-center gap-1 px-4 py-2 text-sm text-primary hover:bg-accent/50 border-b border-border/40"
            onClick={handleBack}
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
        )}

        {/* Category list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : currentItems.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              No categories found
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {currentItems.map((node) => {
                const locked = isInviteOnly(node);
                const isSelected = node.is_leaf && node.id === selectedId;

                return (
                  <button
                    key={node.id}
                    type="button"
                    className={`
                      flex items-center gap-3 w-full px-4 py-3.5 text-left
                      transition-colors
                      ${locked ? "opacity-50 cursor-not-allowed" : "hover:bg-accent/50"}
                      ${isSelected ? "bg-primary/5" : ""}
                    `}
                    disabled={locked}
                    onClick={() => {
                      if (locked) return;
                      if (node.is_leaf) {
                        handleSelectLeaf(node);
                      } else {
                        handleDrillDown(node);
                      }
                    }}
                  >
                    {/* Lock icon for INVITE_ONLY */}
                    {locked && (
                      <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}

                    <div className="flex-1 min-w-0">
                      <span className="text-sm">{node.local_name}</span>
                      {locked && (
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          ต้องได้รับเชิญ
                        </span>
                      )}
                    </div>

                    {/* Right side: chevron for non-leaf, checkmark for selected leaf */}
                    {node.is_leaf ? (
                      isSelected ? (
                        <Check className="h-5 w-5 text-primary shrink-0" />
                      ) : null
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
